import { defaultStoreUtils } from '@databases/redux';
import { selectValueSingle } from '@databases/redux/cellSelections/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { useEffect, useState } from 'react';

import CommentThread, { Thread } from './CommentThread';

interface CommentThreadProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  commentToggled: boolean;
  threads: Thread[] | undefined;
  create?: boolean;
}

const CommentThreads = ({
  contentId,
  fieldId,
  entryId,
  commentToggled,
  threads,
  create,
}: CommentThreadProps) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const [selectedThread, setSelectedThread] = useState<Thread>(undefined);
  const [isCreate, setIsCreate] = useState(!!create);
  const [currentUserId, stringRepresentation] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const field = getDatabaseFieldRequired(database, fieldId);
      const entry = getDatabaseEntryRequired(database, entryId);
      const value = getDatabaseValue(database, field, entry);
      const currentUserId = selectCurrentUserId(state);

      const stringRepresentation =
        value.type !== DatabaseFieldType.ATTACHMENT
          ? getStringRepresentation({ database, field, entry, value }, state)
          : undefined;

      return [currentUserId, stringRepresentation] as const;
    }
  );

  useEffect(() => {
    dispatch(
      selectValueSingle({
        entryId: entryId,
        fieldId: fieldId,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCreate && threads && threads.length) {
      setSelectedThread(threads[0]);
    } else {
      setSelectedThread(undefined);
      setIsCreate(true);
    }
  }, [dispatch, entryId, fieldId, isCreate, threads]);

  return (
    <CommentThread
      contentId={contentId}
      fieldId={fieldId}
      entryId={entryId}
      commentToggled={commentToggled}
      thread={selectedThread}
      currentUserId={currentUserId}
      isCreate={isCreate}
      stringRepresentation={stringRepresentation}
      setIsCreate={setIsCreate}
    />
  );
};

export default CommentThreads;
